/**
 * Syncs OTM Welcome Pack with Loop bundle selections and cart.
 *
 * - Injects the companion into Loop localStorage when bundle items are selected.
 * - Keeps cart in sync when bundle lines are added or removed elsewhere.
 */
(function () {
  const LOOP_STORAGE_PREFIX = 'loop-bundle-selected-variants_';
  const COMPANION_MARKER = '__bundleCompanion';

  const DEFAULT_CONFIG = {
    productId: 10167278928121,
    variantId: 48762178502905,
    companionPropertyKey: '_bundleCompanion',
    companionPropertyValue: 'true',
    syncRetryAttempts: 12,
    syncRetryDelayMs: 250,
    loopVariant: null,
    oneTimeButtonSelector: '#loop-btn-default-one-time',
    frequencyButtonSelector:
      '.loop-bundle-frequency-selector-btn, .loop-frequency-selector .loop-btn',
  };

  const config = Object.assign(
    {},
    DEFAULT_CONFIG,
    window.BUNDLE_COMPANION_CONFIG || {}
  );

  const cartRoot = window.Shopify?.routes?.root || '/';
  const cartMutationPattern = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  let syncing = false;
  let syncQueued = false;
  let loopStorageWriting = false;

  function delay(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function isLoopBundlePage() {
    return window.location.pathname.includes('/a/loop_subscriptions/bundle');
  }

  function getLoopStorageKeys() {
    const keys = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);

      if (key && key.startsWith(LOOP_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }

    return keys;
  }

  function parseLoopBundleData(rawValue) {
    if (!rawValue) {
      return null;
    }

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return null;
    }
  }

  function getCompanionProperties() {
    return {
      [config.companionPropertyKey]: config.companionPropertyValue,
    };
  }

  function hasCompanionProperty(item) {
    const properties = getProperties(item);

    return (
      properties[config.companionPropertyKey] === config.companionPropertyValue
    );
  }

  function isCompanionLoopVariant(variant) {
    if (!variant) {
      return false;
    }

    if (variant[COMPANION_MARKER] === true) {
      return true;
    }

    const properties = variant.properties || {};

    return (
      properties[config.companionPropertyKey] === config.companionPropertyValue
    );
  }

  function isLoopOneTimePurchaseSelected() {
    const oneTimeButton = document.querySelector(config.oneTimeButtonSelector);

    if (!oneTimeButton) {
      return false;
    }

    return (
      oneTimeButton.classList.contains('loop-btn-selected') ||
      oneTimeButton.getAttribute('aria-current') === 'true'
    );
  }

  function cartBundleIsSubscription(items) {
    return items
      .filter(hasBundleProperties)
      .some((item) => Boolean(item.selling_plan_allocation));
  }

  function shouldIncludeCompanion(items) {
    if (isLoopBundlePage()) {
      return !isLoopOneTimePurchaseSelected();
    }

    const bundleItems = (items || []).filter(hasBundleProperties);

    if (bundleItems.length === 0) {
      return false;
    }

    return cartBundleIsSubscription(bundleItems);
  }

  function buildCompanionLoopVariant() {
    if (config.loopVariant) {
      const loopVariant = config.loopVariant;

      return {
        shopifyId: Number(loopVariant.shopifyId || config.variantId),
        variantTitle: loopVariant.variantTitle || 'Default Title',
        price: String(loopVariant.price ?? '0.00'),
        position: loopVariant.position || 9999,
        productVariantId: loopVariant.productVariantId || 0,
        inventoryPolicy: loopVariant.inventoryPolicy || 'deny',
        taxable: loopVariant.taxable ? 1 : 0,
        image: loopVariant.image || '',
        inventoryItemId: Number(loopVariant.inventoryItemId || 0),
        requiresShipping: loopVariant.requiresShipping ? 1 : 0,
        outOfStock: Boolean(loopVariant.outOfStock),
        quantity: 1,
        properties: Object.assign(
          {},
          loopVariant.properties,
          getCompanionProperties()
        ),
        [COMPANION_MARKER]: true,
      };
    }

    return {
      shopifyId: Number(config.variantId),
      variantTitle: 'Default Title',
      price: '0.00',
      position: 9999,
      productVariantId: 0,
      inventoryPolicy: 'deny',
      taxable: 1,
      image: '',
      inventoryItemId: 0,
      requiresShipping: 1,
      outOfStock: false,
      quantity: 1,
      properties: getCompanionProperties(),
      [COMPANION_MARKER]: true,
    };
  }

  function injectCompanionIntoBundleData(data) {
    if (!data || !Array.isArray(data.selectedVariants)) {
      return data;
    }

    const selectedVariants = data.selectedVariants.filter(
      (variant) => !isCompanionLoopVariant(variant)
    );

    if (selectedVariants.length === 0 || !shouldIncludeCompanion()) {
      return Object.assign({}, data, { selectedVariants });
    }

    return Object.assign({}, data, {
      selectedVariants: selectedVariants.concat(buildCompanionLoopVariant()),
    });
  }

  function writeLoopStorageValue(key, value) {
    loopStorageWriting = true;

    try {
      localStorage.setItem(key, value);
    } finally {
      loopStorageWriting = false;
    }
  }

  function syncLoopLocalStorageValue(rawValue) {
    const data = parseLoopBundleData(rawValue);

    if (!data) {
      return rawValue;
    }

    return JSON.stringify(injectCompanionIntoBundleData(data));
  }

  function syncAllLoopLocalStorage() {
    getLoopStorageKeys().forEach((key) => {
      const rawValue = localStorage.getItem(key);
      const syncedValue = syncLoopLocalStorageValue(rawValue);

      if (syncedValue && syncedValue !== rawValue) {
        writeLoopStorageValue(key, syncedValue);
      }
    });
  }  
 
  function hookLoopLocalStorage() {
    if (window.__bundleCompanionStorageHooked || !window.localStorage) {
      return;
    }

    window.__bundleCompanionStorageHooked = true;

    const storage = window.localStorage;
    const originalSetItem = storage.setItem.bind(storage);
    const originalGetItem = storage.getItem.bind(storage);

    storage.setItem = function (key, value) {
      if (
        !loopStorageWriting &&
        typeof key === 'string' &&
        key.startsWith(LOOP_STORAGE_PREFIX)
      ) {
        value = syncLoopLocalStorageValue(value);
      }

      return originalSetItem(key, value);
    };

    storage.getItem = function (key) {
      const value = originalGetItem(key);

      if (
        typeof key === 'string' &&
        key.startsWith(LOOP_STORAGE_PREFIX) &&
        value
      ) {
        return syncLoopLocalStorageValue(value);
      }

      return value;
    };

    window.addEventListener('storage', (event) => {
      if (
        event.key &&
        event.key.startsWith(LOOP_STORAGE_PREFIX) &&
        event.newValue
      ) {
        const syncedValue = syncLoopLocalStorageValue(event.newValue);

        if (syncedValue !== event.newValue) {
          writeLoopStorageValue(event.key, syncedValue);
        }
      }
    });
  }

  function watchLoopBundleSelections() {
    if (!isLoopBundlePage() || window.__bundleCompanionSelectionWatcher) {
      return;
    }

    window.__bundleCompanionSelectionWatcher = true;

    syncAllLoopLocalStorage();

    window.setInterval(syncAllLoopLocalStorage, 500);

    document.addEventListener(
      'click',
      (event) => {
        const frequencyButton = event.target.closest(
          config.frequencyButtonSelector
        );

        if (frequencyButton) {
          window.setTimeout(syncAllLoopLocalStorage, 0);
          return;
        }

        window.setTimeout(syncAllLoopLocalStorage, 0);
      },
      true
    );
  }

  function getProperties(item) {
    return item?.properties || {};
  }

  function hasNonEmptyProperty(properties, key) {
    const value = properties[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  }

  function isCompanionItem(item) {
    return hasCompanionProperty(item);
  }

  function hasBundleProperties(item) {
    if (hasCompanionProperty(item)) {
      return false;
    }

    const properties = getProperties(item);

    return (
      hasNonEmptyProperty(properties, '_bundleId') &&
      hasNonEmptyProperty(properties, '_bundleName')
    );
  }

  function cartHasBundleItems(items) {
    return items.some(hasBundleProperties);
  }

  function getCompanionItems(items) {
    return items.filter(isCompanionItem);
  }

  function isCartInSync(items) {
    const hasBundle = cartHasBundleItems(items);
    const hasCompanion = getCompanionItems(items).length > 0;
    const includeCompanion = shouldIncludeCompanion(items);

    if (!hasBundle) {
      return !hasCompanion;
    }

    return includeCompanion ? hasCompanion : !hasCompanion;
  }

  async function fetchCart() {
    const response = await fetch(`${cartRoot}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart');
    }

    return response.json();
  }

  async function addCompanionProduct() {
    const response = await fetch(`${cartRoot}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(config.variantId),
            quantity: 1,
            properties: getCompanionProperties(),
          },
        ],
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.description || data?.message || 'Unable to add companion product'
      );
    }

    return data;
  }

  async function removeCompanionProducts(items) {
    const updates = {};

    items.forEach((item) => {
      updates[item.key] = 0;
    });

    const response = await fetch(`${cartRoot}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.description ||
          data?.message ||
          'Unable to remove companion product'
      );
    }

    return data;
  }

  function isCartPage() {
    return /\/cart(\/|$|\?)/.test(window.location.pathname);
  }

  function waitForLoopBundleInit() {
    return new Promise((resolve) => {
      if (!isCartPage()) {
        resolve();
        return;
      }

      const startedAt = Date.now();
      const maxWaitMs = 5000;

      const check = () => {
        const container = document.querySelector('.LOOP_BUNDLE_CONTAINER_CLASS');
        const hasBundleMarkup =
          container &&
          container.querySelector(
            '.cart-item, .cart-form-item, tr.cart-item, [id^="CartBundleItem-"]'
          );

        if (hasBundleMarkup || Date.now() - startedAt >= maxWaitMs) {
          resolve();
          return;
        }

        window.setTimeout(check, 100);
      };

      window.setTimeout(check, 100);
    });
  }

  async function refreshThemeCart(cartChanged) {
    if (isCartPage()) {
      if (cartChanged) {
        window.location.reload();
        return;
      }

      if (typeof window.syncHeaderCart === 'function') {
        await window.syncHeaderCart();
      }

      return;
    }

    if (typeof window.refreshCart === 'function') {
      window.refreshCart();
      return;
    }

    if (typeof window.syncHeaderCart === 'function') {
      await window.syncHeaderCart();
    }
  }

  async function syncBundleCompanionProductOnce() {
    const cart = await fetchCart();
    const items = cart.items || [];
    const hasBundle = cartHasBundleItems(items);
    const companionItems = getCompanionItems(items);
    const includeCompanion = shouldIncludeCompanion(items);
    let cartChanged = false;

    if (hasBundle && includeCompanion && companionItems.length === 0) {
      await addCompanionProduct();
      cartChanged = true;
    } else if ((!hasBundle || !includeCompanion) && companionItems.length > 0) {
      await removeCompanionProducts(companionItems);
      cartChanged = true;
    }

    return cartChanged;
  }

  async function ensureCompanionSynced(options = {}) {
    const attempts = options.attempts ?? config.syncRetryAttempts;
    const retryDelayMs = options.retryDelayMs ?? config.syncRetryDelayMs;
    let cartChanged = false;

    syncAllLoopLocalStorage();

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (syncing) {
        await delay(retryDelayMs);
        continue;
      }

      syncing = true;

      try {
        cartChanged = (await syncBundleCompanionProductOnce()) || cartChanged;
        const cart = await fetchCart();

        if (isCartInSync(cart.items || [])) {
          if (cartChanged) {
            await refreshThemeCart(true);
            document.dispatchEvent(new CustomEvent('bundle-companion-synced'));
          }

          return cartChanged;
        }
      } catch (error) {
        console.warn('[bundle-companion-product]', error);
      } finally {
        syncing = false;
      }

      if (attempt < attempts - 1) {
        await delay(retryDelayMs);
      }
    }

    return cartChanged;
  }

  async function syncBundleCompanionProduct() {
    if (syncing) {
      syncQueued = true;
      return;
    }

    try {
      await ensureCompanionSynced({ attempts: 1 });
    } finally {
      if (syncQueued) {
        syncQueued = false;
        syncBundleCompanionProduct();
      }
    }
  }

  function shouldAwaitCartSync(url, method) {
    if (syncing) {
      return false;
    }

    if (!cartMutationPattern.test(url)) {
      return false;
    }

    const normalizedMethod = (method || 'GET').toUpperCase();
    return normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD';
  }

  async function handleCartMutationResponse(response) {
    if (!response?.ok) {
      return;
    }

    syncAllLoopLocalStorage();
    await delay(100);
    await ensureCompanionSynced();
  }

  function hookCartFetch() {
    if (window.__bundleCompanionFetchHooked) {
      return;
    }

    window.__bundleCompanionFetchHooked = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = function (...args) {
      const request = args[0];
      const init = args[1] || {};
      const url =
        typeof request === 'string'
          ? request
          : request instanceof Request
            ? request.url
            : '';
      const method =
        init.method ||
        (request instanceof Request ? request.method : undefined);

      const awaitSync = shouldAwaitCartSync(url, method);

      return originalFetch(...args).then(async (response) => {
        if (awaitSync) {
          await handleCartMutationResponse(response);
        }

        return response;
      });
    };
  }

  function hookCartXHR() {
    if (window.__bundleCompanionXHRHooked || !window.XMLHttpRequest) {
      return;
    }

    window.__bundleCompanionXHRHooked = true;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__bundleCompanionMethod = method;
      this.__bundleCompanionUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      const xhr = this;
      const url = xhr.__bundleCompanionUrl || '';
      const method = xhr.__bundleCompanionMethod || 'GET';
      const awaitSync = shouldAwaitCartSync(url, method);

      if (awaitSync) {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            handleCartMutationResponse({ ok: true });
          }
        });
      }

      return originalSend.apply(this, args);
    };
  }

  function hookLoopAddToCartButton() {
    if (window.__bundleCompanionLoopButtonHooked) {
      return;
    }

    window.__bundleCompanionLoopButtonHooked = true;

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.loop-btn-add-to-cart');

        if (!button) {
          return;
        }

        syncAllLoopLocalStorage();

        window.setTimeout(() => {
          ensureCompanionSynced();
        }, 0);
      },
      true
    );
  }

  function hookRemoveBundleLoop() {
    if (typeof window.removeBundleLoop !== 'function') {
      return;
    }

    if (window.removeBundleLoop.__bundleCompanionWrapped) {
      return;
    }

    const originalRemoveBundleLoop = window.removeBundleLoop;

    window.removeBundleLoop = async function (...args) {
      await originalRemoveBundleLoop.apply(this, args);
      await ensureCompanionSynced();
    };

    window.removeBundleLoop.__bundleCompanionWrapped = true;
  }

  async function runCartPageCompanionSync() {
    await waitForLoopBundleInit();
    await syncBundleCompanionProduct();
  }

  function bindEvents() {
    document.addEventListener('DOMContentLoaded', () => {
      hookLoopLocalStorage();
      watchLoopBundleSelections();
      hookRemoveBundleLoop();
      hookLoopAddToCartButton();

      if (isCartPage()) {
        runCartPageCompanionSync();
        return;
      }

      syncBundleCompanionProduct();
    });

    document.addEventListener('cart-updated', () => {
      if (isCartPage()) {
        runCartPageCompanionSync();
        return;
      }

      syncBundleCompanionProduct();
    });

    document.addEventListener('bundle-companion-synced', () => {
      hookRemoveBundleLoop();
    });

    window.addEventListener('pageshow', () => {
      syncAllLoopLocalStorage();

      if (isCartPage()) {
        runCartPageCompanionSync();
        return;
      }

      syncBundleCompanionProduct();
    });
  }

  hookLoopLocalStorage();
  hookCartFetch();
  hookCartXHR();
  hookLoopAddToCartButton();
  bindEvents();
  window.syncBundleCompanionProduct = syncBundleCompanionProduct;
  window.ensureBundleCompanionSynced = ensureCompanionSynced;
  window.syncLoopBundleCompanionStorage = syncAllLoopLocalStorage;
})();
